// src/scenes/global/Sidebar.jsx
import React, { useState } from "react";
import {
    Box,
    Drawer,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    IconButton,
    Toolbar,
    Divider,
    useMediaQuery,
    Collapse,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { NavLink, useLocation, Outlet } from "react-router-dom";

import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import DescriptionIcon from "@mui/icons-material/Description";
import PetsIcon from "@mui/icons-material/Pets";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";

const DRAWER_WIDTH = 260;
const COLLAPSED_WIDTH = 64;

const NAV_ITEMS = [
    { label: "Home", path: "/home", icon: <HomeOutlinedIcon />, color: "primary" },
    { label: "Inbox", path: "/inbox", icon: <MailOutlineIcon />, color: "info" },
    { label: "Work Queue", path: "/work-queue", icon: <WorkOutlineIcon />, color: "secondary" },
];

const StyledNavLink = styled(NavLink, {
    shouldForwardProp: (prop) => prop !== "collapsed",
})(({ theme, collapsed }) => ({
    textDecoration: "none",
    color: theme.palette.text.primary,
    "& .MuiListItemText-root": {
        display: collapsed ? "none" : "block",
    },
}));

export default function Sidebar() {
    const theme = useTheme();
    const location = useLocation();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const [collapsed, setCollapsed] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [formsOpen, setFormsOpen] = useState(true);

    const toggleDesktop = () => setCollapsed((c) => !c);
    const toggleMobile = () => setMobileOpen((o) => !o);
    const toggleForms = () => setFormsOpen((o) => !o);

    const drawerContent = (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "100vh",
                width: collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
                transition: theme.transitions.create("width", {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.enteringScreen,
                }),
                boxShadow: theme.shadows[4],
            }}
        >
            {/* Logo + collapse button */}
            <Toolbar
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: collapsed ? "center" : "space-between",
                    px: collapsed ? 1 : 2,
                    backgroundColor: theme.palette.primary.main,      // ← solid navy background
                    color: theme.palette.primary.contrastText,         // ← ensure icons/text are white
                }}
            >
                {!collapsed && (
                    <Box
                        component="img"
                        src="/logo-dph.png"
                        alt="SC DPH Logo"
                        sx={{ height: 64 }}
                    />
                )}
                <IconButton
                    onClick={toggleDesktop}
                    sx={{ color: theme.palette.primary.contrastText }} // ensure toggle arrow is white
                >
                    {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                </IconButton>
            </Toolbar>

            <Divider />

            {/* Navigation */}
            <Box sx={{ overflowY: "auto", flexGrow: 1 }}>
                <List disablePadding>
                    {NAV_ITEMS.map(({ label, path, icon, color }, idx) => {
                        const active = location.pathname === path;
                        return (
                            <StyledNavLink key={idx} to={path} collapsed={collapsed ? 1 : 0}>
                                <ListItemButton
                                    onClick={() => isMobile && toggleMobile()}
                                    sx={{
                                        pl: collapsed ? 1 : 2.5,
                                        justifyContent: collapsed ? "center" : "flex-start",
                                        mb: 0.5,
                                        borderRadius: theme.shape.borderRadius,
                                        borderLeft: collapsed
                                            ? 0
                                            : `4px solid ${active
                                                ? theme.palette[color].main
                                                : "transparent"
                                            }`,
                                        backgroundColor: active
                                            ? theme.palette.action.selected
                                            : "transparent",
                                        "&:hover": {
                                            backgroundColor: theme.palette.action.hover,
                                        },
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: collapsed ? 0 : 36,
                                            color: theme.palette[color].main,
                                            justifyContent: "center",
                                        }}
                                    >
                                        {icon}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={label}
                                        primaryTypographyProps={{ fontWeight: 600 }}
                                    />
                                </ListItemButton>
                            </StyledNavLink>
                        );
                    })}

                    {/* Forms Section */}
                    <ListItemButton
                        onClick={toggleForms}
                        sx={{
                            pl: collapsed ? 1 : 2.5,
                            justifyContent: collapsed ? "center" : "flex-start",
                            mb: 0.5,
                        }}
                    >
                        <ListItemIcon
                            sx={{
                                minWidth: collapsed ? 0 : 36,
                                justifyContent: "center",
                            }}
                        >
                            <DescriptionIcon />
                        </ListItemIcon>
                        <ListItemText
                            primary="Forms"
                            primaryTypographyProps={{ fontWeight: 600 }}
                        />
                        {!collapsed && (formsOpen ? <ExpandLess /> : <ExpandMore />)}
                    </ListItemButton>

                    <Collapse in={formsOpen} timeout="auto" unmountOnExit>
                        <List disablePadding>
                            <StyledNavLink to="/animal-incident" collapsed={collapsed ? 1 : 0}>
                                <ListItemButton
                                    onClick={() => isMobile && toggleMobile()}
                                    sx={{
                                        pl: collapsed ? 1 : 4,
                                        justifyContent: collapsed ? "center" : "flex-start",
                                        mb: 0.5,
                                        borderRadius: theme.shape.borderRadius,
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: collapsed ? 0 : 36,
                                            justifyContent: "center",
                                        }}
                                    >
                                        <PetsIcon color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="Animal Incident" />
                                </ListItemButton>
                            </StyledNavLink>
                            {/* …other form links */}
                        </List>
                    </Collapse>
                </List>
            </Box>
        </Box>
    );

    return (
        <>
            {isMobile && (
                <IconButton
                    onClick={toggleMobile}
                    sx={{
                        position: "fixed",
                        top: 16,
                        left: 16,
                        zIndex: theme.zIndex.drawer + 1,
                        backgroundColor: theme.palette.background.paper,
                        boxShadow: theme.shadows[2],
                    }}
                >
                    <MenuIcon />
                </IconButton>
            )}

            <Drawer
                variant={isMobile ? "temporary" : "permanent"}
                open={isMobile ? mobileOpen : true}
                onClose={toggleMobile}
                ModalProps={{ keepMounted: true }}
                sx={{
                    "& .MuiDrawer-paper": {
                        width: collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
                        boxSizing: "border-box",
                        overflowX: "hidden",
                        transition: theme.transitions.create("width", {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.leavingScreen,
                        }),
                    },
                }}
            >
                {drawerContent}
            </Drawer>

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    ml: isMobile
                        ? 0
                        : collapsed
                            ? COLLAPSED_WIDTH
                            : DRAWER_WIDTH,
                    transition: theme.transitions.create(["margin"], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                }}
            >
                <Toolbar />
                <Outlet />
            </Box>
        </>
    );
}
